import React, { useState, useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  TextField, 
  Button, 
  Box, 
  CircularProgress, 
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  PhotoCamera, 
  Save,
  DeleteOutline
} from '@mui/icons-material';
import { UserService } from '../services/UserService';
import { getConfig } from '../configService';
import queryString from "query-string";
import {useNavigate} from "react-router-dom";
import {Helmet} from "react-helmet";

function UserProfile() {
  const parsedQueryString = queryString.parse(window.location.search);
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  // Profile picture states
  const [profilePicture, setProfilePicture] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const fileInputRef = useRef(null);
  const config = getConfig();

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      let profileResponse;
      try {
        profileResponse = await UserService.getUserProfile();
      } catch (error){
        profileResponse = {}
      }
      const profileData = (profileResponse?.status === 200)?profileResponse.data:{};
      
      // Initialize form data with profile data
      setFormData({
        firstName: profileData?.name?.split(" ")[0] || keycloak.tokenParsed.given_name || '',
        lastName: profileData?.name?.split(" ")[1] || keycloak.tokenParsed.family_name ||'',
        email: profileData?.email || keycloak.tokenParsed.email ||'',
        phone: profileData?.phoneNumber || '',
        address: profileData?.address || '',
        city: profileData?.city || '',
        state: profileData?.state || '',
        zip: profileData?.postalCode || ''
      });

      // Set profile picture if exists
      if (profileData.thumbnail != null && profileData.thumbnail.length > 0) {
        let src= config.imageBaseUrl + profileData.id + "/" + profileData.thumbnail + ".jpg"
        setPicturePreview( src);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setLoading(false);
      showSnackbar('Failed to load profile', 'error');
    }
  };

  useEffect(() => {
    if (keycloak && keycloak.authenticated) {
      loadUserProfile();
    }
  }, [keycloak]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaveLoading(true);

      const userData = {
        "id": `${keycloak.tokenParsed.sub}`,
        "name": `${formData.firstName} ${formData.lastName}`,
        "address": `${formData.address}`,
        "city": `${formData.city}`,
        "state": `${formData.state}`,
        "postalCode": `${formData.zip}`,
        "phoneNumber": `${formData.phone}`,
        "email": `${formData.email}`,
        "thumbnail": "",
        "rating": 0
      }

      var response = await UserService.updateUserProfile(userData);

      if (response.status !== 200) {
        setSaveLoading(false);
        console.log(response);
        showSnackbar('Failed to update profile. Check the data and try again.', 'error');
        return;
      }

      // If there's a new profile picture, upload it
      if (profilePicture) {
        await uploadProfilePicture();
      }
      
      await loadUserProfile(); // Reload profile after update
      
      // Dispatch an event to notify header component to update avatar
      const profileUpdatedEvent = new Event('profileUpdated');
      window.dispatchEvent(profileUpdatedEvent);
      
      setSaveLoading(false);
      showSnackbar('Profile updated successfully!', 'success');
      if(parsedQueryString.redirectTo !== undefined) {
        navigate(parsedQueryString.redirectTo);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveLoading(false);
      showSnackbar('Failed to update profile. Please try again.', 'error');
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    
    // Validate file size (5MB max)
    if (file && file.size > 5 * 1024 * 1024) {
      showSnackbar('File size exceeds 5MB limit', 'error');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (file && !validTypes.includes(file.type)) {
      showSnackbar('Invalid file type. Please use JPEG, PNG, GIF, or WEBP', 'error');
      return;
    }
    
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfilePicture = async () => {
    if (!profilePicture) return;
    
    try {
      setUploadingPicture(true);
      
      // Step 1: Get upload URL
      const uploadUrlResponse = await UserService.generateProfilePictureUploadURL();
      if (uploadUrlResponse.status !== 200) {
        console.error('Failed to get upload URL');
        setUploadingPicture(false);
        showSnackbar('Failed to get upload URL', 'error');
        return false;
      }
      
      const { uploadUrl, imageId } = uploadUrlResponse.data;
      
      // Step 2: Upload the image
      const uploadResponse = await UserService.uploadProfilePicture(uploadUrl, profilePicture);
      if (uploadResponse.status !== 200 && uploadResponse.status !== 201) {
        console.error('Failed to upload image');
        setUploadingPicture(false);
        showSnackbar('Failed to upload image', 'error');
        return false;
      }
      
      // Step 3: Complete upload
      const completeResponse = await UserService.completeProfilePictureUpload(imageId);
      if (completeResponse.status !== 200) {
        console.error('Failed to complete upload');
        setUploadingPicture(false);
        showSnackbar('Failed to complete upload', 'error');
        return false;
      }
      
      setProfilePicture(null);
      setUploadingPicture(false);
      return true;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setUploadingPicture(false);
      showSnackbar('Failed to upload profile picture', 'error');
      return false;
    }
  };

  const handleDeleteProfilePicture = () => {
    setProfilePicture(null);
    setPicturePreview(null);
    // Here you would add code to delete the profile picture from the server
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  if (loading) {
    return (
      <Container sx={{ py: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Helmet>
          <title>PairCare - User Profile</title>
        </Helmet>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ py: 5 }}>
      <Helmet>
        <title>PairCare - User Profile</title>
      </Helmet>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        User Profile
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Profile Picture */}
            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar 
                  src={picturePreview} 
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}
                >
                  {formData.firstName && formData.lastName ? 
                    `${formData.firstName[0]}${formData.lastName[0]}` : ''}
                </Avatar>
                
                <input
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  type="file"
                  hidden
                  ref={fileInputRef}
                  onChange={handleProfilePictureChange}
                />
                
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  right: 0, 
                  bgcolor: 'background.paper', 
                  borderRadius: '50%',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  border: '1px solid #e0e0e0'
                }}>
                  <Tooltip title="Change profile picture">
                    <IconButton 
                      color="primary" 
                      aria-label="upload picture" 
                      component="span"
                      onClick={triggerFileInput}
                      size="small"
                    >
                      <PhotoCamera />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              {picturePreview && (
                <Button 
                  startIcon={<DeleteOutline />} 
                  color="error" 
                  onClick={handleDeleteProfilePicture} 
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Remove Picture
                </Button>
              )}
              
            </Grid>
            
            {/* Form Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                startIcon={<Save />}
                disabled={saveLoading || uploadingPicture}
              >
                {(saveLoading || uploadingPicture) ? 'Saving...' : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default UserProfile; 